import { useMemo, useState } from 'react';
import {
  useGetClaimTimesheet,
  useCreateClaimTimesheetExportToken,
  useGetProjects,
  getGetProjectsQueryKey,
  type ClaimTimesheetDailyRow,
  type ClaimTimesheetRateCardEntry,
} from '@edison/api-client-react';
import { Download, Info } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { Card, Metric } from '@/components/ui/card.js';
import { SkeletonCard } from '@/components/ui/skeleton.js';
import { InlineError } from '@/components/ui/inline-error.js';
import { Badge } from '@/components/ui/badge.js';
import { ClaimModeBadge } from '@/components/claim-mode-badge.js';
import { SlidePanel } from '@/components/ui/slide-panel.js';

// The export URL is minted relative to the API; a plain navigation needs the absolute base (same
// convention as the portal CSV exports in audit-portal/portal-expenditure.tsx).
const apiBase = typeof window !== 'undefined' ? (import.meta.env['VITE_API_BASE_URL'] ?? '') : '';

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
}

function formatHours(n: number): string {
  return `${(Math.round(n * 10) / 10).toFixed(1)}h`;
}

function monthLabel(monthKey: string): string {
  const [y, m] = monthKey.split('-').map((v) => parseInt(v, 10));
  return new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric' }).format(new Date(y ?? 1970, (m ?? 1) - 1, 1));
}

function monthShortLabel(monthKey: string): string {
  const [y, m] = monthKey.split('-').map((v) => parseInt(v, 10));
  const short = new Intl.DateTimeFormat('en-AU', { month: 'short' }).format(new Date(y ?? 1970, (m ?? 1) - 1, 1));
  return `${short} ${String((y ?? 1970) % 100).padStart(2, '0')}`;
}

function dowLabel(dateStr: string): string {
  return new Intl.DateTimeFormat('en-AU', { weekday: 'short' }).format(new Date(`${dateStr}T00:00:00Z`));
}

// Enumerate every FY month key (July of the FY's start year through June of its end year), in
// calendar order, REGARDLESS of whether any data exists for that month — full-FY coverage is
// requirement #1: empty months render honestly (quiet cells), never a page that only shows the
// months work happened to land in.
function fyMonthKeys(incomeYear: string): string[] {
  const match = /^FY(\d{4})$/.exec(incomeYear);
  const endYear = match ? parseInt(match[1]!, 10) : new Date().getFullYear();
  const startYear = endYear - 1;
  const keys: string[] = [];
  for (let i = 0; i < 12; i++) {
    const monthNum = ((6 + i) % 12) + 1; // 7,8,...,12,1,...,6
    const year = i < 6 ? startYear : endYear;
    keys.push(`${year}-${String(monthNum).padStart(2, '0')}`);
  }
  return keys;
}

interface PersonSummary {
  userId: string;
  name: string;
  sessions: number;
  hasSessionData: boolean;
  rdHours: number;
  rdExpenditure: number;
  hourlyRate: number | null; // null = no configured rate (Brian/Shaun-style — effort with $0)
}

// Per-person totals for the overview strip — the artefact leads with a summary before any detail,
// and this doubles as the person selector (clicking a card selects that person's month/day view).
function summarizeByPerson(
  dailyRows: ClaimTimesheetDailyRow[],
  rateCard: ClaimTimesheetRateCardEntry[],
): PersonSummary[] {
  const byUser = new Map<string, PersonSummary>();
  for (const r of dailyRows) {
    let p = byUser.get(r.userId);
    if (!p) {
      p = { userId: r.userId, name: r.name, sessions: 0, hasSessionData: false, rdHours: 0, rdExpenditure: 0, hourlyRate: null };
      byUser.set(r.userId, p);
    }
    if (r.sessionCount !== undefined) { p.sessions += r.sessionCount; p.hasSessionData = true; }
    p.rdHours += r.rdHours;
    p.rdExpenditure += r.rdExpenditure;
  }
  const rateByUser = new Map(rateCard.map((rc) => [rc.userId, rc.hourlyRate]));
  for (const p of byUser.values()) p.hourlyRate = rateByUser.get(p.userId) ?? null;
  return [...byUser.values()].sort((a, b) => b.rdExpenditure - a.rdExpenditure || a.name.localeCompare(b.name));
}

// Fixed verbatim copy (Stage 1 apportionment evidence store spec §7.4, wording updated Task 16 to
// match the 18 Sep artefact's own methodology): the daily rows on this tab are a reviewer true-up
// estimate, NOT a second computation of the claim — the claim dollar figure is loaded-salary based
// (spread across the whole FY) and is reconciled in the Expenditure tab, not by summing this table.
// Exported so the component test can assert on it verbatim.
export const APPORTIONMENT_METHODOLOGY_NOTE =
  'Claimed labour is spread by work sessions, not hours — FY2026 durations are stitching-inflated and not used for weighting. Hours shown here are for reference only. Claimed labour = loaded salary × session weight × adopted share — see the reconciliation in the Expenditure tab. Daily dollar values are not summed to the claim.';

interface DayPanelData { date: string; personName: string; rows: ClaimTimesheetDailyRow[] }

// Day drill-down: project attribution behind ONE person's ONE day, from data already loaded
// client-side (no extra fetch) — a SlidePanel, never a modal, per the UI standard.
function TimesheetDayPanel({ day, onClose }: { day: DayPanelData | null; onClose: () => void }) {
  const rows = day?.rows ?? [];
  const totalExpenditure = rows.reduce((s, r) => s + r.rdExpenditure, 0);
  const totalHours = rows.reduce((s, r) => s + r.rdHours, 0);
  const anySessionData = rows.some((r) => r.sessionCount !== undefined);
  const totalSessions = rows.reduce((s, r) => s + (r.sessionCount ?? 0), 0);

  return (
    <SlidePanel open={day !== null} onClose={onClose} title={day ? `${day.personName} — ${day.date}` : 'Day'} width="w-[520px]">
      <div className="space-y-4 p-5">
        {day && (
          <>
            <div className="flex flex-wrap items-baseline gap-2 text-sm text-muted-foreground">
              <span>{dowLabel(day.date)}</span>
              <span aria-hidden="true">&middot;</span>
              <span className="font-mono tabular-nums font-medium text-foreground">{formatCurrency(totalExpenditure)}</span>
              <span>R&amp;D value</span>
              {anySessionData && (
                <>
                  <span aria-hidden="true">&middot;</span>
                  <span>{totalSessions} session{totalSessions === 1 ? '' : 's'}</span>
                </>
              )}
              <span aria-hidden="true">&middot;</span>
              <span>{formatHours(totalHours)} (reference only)</span>
            </div>
            <ul className="space-y-1.5">
              {[...rows].sort((a, b) => b.rdExpenditure - a.rdExpenditure).map((r) => (
                <li key={r.projectId} className="rounded-lg border px-3 py-2 text-sm flex items-center justify-between gap-3">
                  <span className="min-w-0 truncate font-medium">{r.projectName}</span>
                  <span className="shrink-0 flex items-center gap-3 font-mono tabular-nums text-xs text-muted-foreground">
                    {r.sessionCount !== undefined && <span>{r.sessionCount} sess</span>}
                    <span>{formatHours(r.rdHours)}</span>
                    <span className="font-medium text-foreground">{formatCurrency(r.rdExpenditure)}</span>
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </SlidePanel>
  );
}

interface Props {
  claimId: string;
  incomeYear: string;
}

/**
 * Claim-page Timesheet tab (Stage 1 apportionment evidence store, Task 13; rebuilt to match the
 * 18 Sep 2026 working-paper visual): a thin read-only view over the SAME buildLabourTimesheet
 * engine the reviewer portal's labour-timesheet reader uses — one calculation, two surfaces. For
 * a locked apportionment claim this renders the frozen lock-snapshot's dailyRows verbatim
 * (basis: 'snapshot'); everything else is a live rebuild. Full financial year, always — every FY
 * month renders (quiet cells when empty), one person's view at a time, with a per-day drill-down.
 */
export function ClaimTimesheetTab({ claimId, incomeYear }: Props) {
  const { data, isLoading, error, refetch } = useGetClaimTimesheet(claimId);
  // Only needed to resolve human-readable names for apportionmentAwaitingProjects (which carries
  // project ids, not names) — a secondary, non-blocking lookup.
  const projectsQuery = useGetProjects({ incomeYear }, { query: { queryKey: getGetProjectsQueryKey({ incomeYear }) } });

  const timesheet = data?.data;
  const dailyRows = useMemo(() => timesheet?.dailyRows ?? [], [timesheet?.dailyRows]);
  const rateCard = useMemo(() => timesheet?.rateCard ?? [], [timesheet?.rateCard]);
  const people = useMemo(() => summarizeByPerson(dailyRows, rateCard), [dailyRows, rateCard]);
  const monthKeys = useMemo(() => fyMonthKeys(incomeYear), [incomeYear]);

  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<DayPanelData | null>(null);
  const [exportError, setExportError] = useState('');
  const exportMutation = useCreateClaimTimesheetExportToken();
  // Mint-then-navigate: the mint validates the export server-side (refusals come back here as a
  // message), then a new tab issues the plain GET so the server's Content-Disposition filename is
  // honoured in every browser.
  const downloadTimesheetCsv = () => {
    setExportError('');
    exportMutation.mutate({ id: claimId }, {
      onSuccess: (res) => { window.open(`${apiBase}${res.data.url}`, '_blank', 'noopener,noreferrer'); },
      onError: (err) => {
        const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
        setExportError(msg ?? 'Failed to prepare the timesheet export. Please try again.');
      },
    });
  };

  // Every hook above this point runs unconditionally on every render — the early loading/error
  // returns below must come AFTER all hooks (Rules of Hooks), so the derived-state computations
  // that feed the success view also live here, before those returns, even though they're only
  // rendered in the success branch.
  const activePersonId = selectedPersonId && people.some((p) => p.userId === selectedPersonId)
    ? selectedPersonId
    : (people[0]?.userId ?? null);

  const personRowsByDate = useMemo(() => {
    const byDate = new Map<string, ClaimTimesheetDailyRow[]>();
    if (!activePersonId) return byDate;
    for (const r of dailyRows) {
      if (r.userId !== activePersonId) continue;
      const arr = byDate.get(r.date) ?? [];
      arr.push(r);
      byDate.set(r.date, arr);
    }
    return byDate;
  }, [dailyRows, activePersonId]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (error) {
    return <InlineError message="Failed to load the timesheet." onRetry={() => void refetch()} />;
  }

  const isApportionment = timesheet?.mode === 'apportionment';
  const awaitingProjectIds = timesheet?.apportionmentAwaitingProjects ?? [];
  const projectNameById = new Map((projectsQuery.data?.data?.items ?? []).map((p) => [p.id, p.name]));
  const activePerson = people.find((p) => p.userId === activePersonId) ?? null;
  const grandTotalExpenditure = dailyRows.reduce((s, r) => s + r.rdExpenditure, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-medium">Timesheet</h2>
        <div className="flex flex-wrap items-center gap-2">
          <ClaimModeBadge mode={timesheet?.mode} locked={timesheet?.basis === 'snapshot'} />
          {isApportionment && (
            <Button variant="secondary" size="sm" onClick={downloadTimesheetCsv} disabled={exportMutation.isPending}>
              <Download className="h-3.5 w-3.5 mr-1.5 inline" aria-hidden="true" />
              {exportMutation.isPending ? 'Preparing…' : 'Download timesheet (CSV)'}
            </Button>
          )}
        </div>
      </div>
      {exportError && (
        <p role="alert" className="text-xs text-destructive">{exportError}</p>
      )}

      {/* Rule Zero: a zero timesheet caused by unadopted R&D shares is always explained by name,
          never a bare unexplained empty table. */}
      {awaitingProjectIds.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-300">
          Timesheet withheld — R&amp;D shares not yet adopted for:{' '}
          {awaitingProjectIds.map((id) => projectNameById.get(id) ?? id.slice(0, 8)).join(', ')}.
        </div>
      )}

      {dailyRows.length === 0 ? (
        awaitingProjectIds.length === 0 && (
          <Card variant="static" className="p-8 text-center">
            <p className="text-sm text-muted-foreground">No approved labour captured yet for this claim.</p>
          </Card>
        )
      ) : (
        <>
          {/* Overview strip — per-person totals lead the detail, mirroring the working-paper's
              own structure. Cards double as the person selector. */}
          <Card variant="static" className="p-5 space-y-4">
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
              <Metric label={`R&D labour value — ${incomeYear}`} value={formatCurrency(grandTotalExpenditure)} />
              <p className="text-xs text-muted-foreground max-w-[40ch]">
                Reviewer true-up basis: hours × rate, per person per day.
              </p>
            </div>

            {isApportionment && (
              <div
                role="note"
                className="rounded border border-blue-300 bg-blue-50 dark:bg-blue-950/30 px-3 py-2 text-xs text-blue-900 dark:text-blue-300"
              >
                {APPORTIONMENT_METHODOLOGY_NOTE}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4" role="tablist" aria-label="Select person">
              {people.map((p) => {
                const active = p.userId === activePersonId;
                return (
                  <button
                    key={p.userId}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setSelectedPersonId(p.userId)}
                    className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors ${
                      active ? 'border-primary bg-primary/5' : 'border-hairline hover:bg-accent'
                    }`}
                  >
                    <div className="truncate font-medium text-sm text-foreground">{p.name}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono tabular-nums text-muted-foreground">
                      <span className="text-foreground font-medium">{formatCurrency(p.rdExpenditure)}</span>
                      {p.hasSessionData && <span>{p.sessions} sess</span>}
                      <span>{formatHours(p.rdHours)} ref.</span>
                    </div>
                    {p.hourlyRate === null ? (
                      <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400">
                        <Info className="h-3 w-3 shrink-0" aria-hidden="true" />
                        No configured rate — effort shown, $0
                      </div>
                    ) : (
                      <div className="mt-1 text-[11px] text-muted-foreground">{formatCurrency(p.hourlyRate)}/hr</div>
                    )}
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Month quick-jump — 12 months always present, scrollable/navigable rather than a
              paginated flat table (requirement #1). */}
          <nav aria-label="Jump to month" className="flex flex-wrap gap-1">
            {monthKeys.map((mk) => (
              <a
                key={mk}
                href={`#month-${mk}`}
                className="rounded-full border border-hairline px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                {monthShortLabel(mk)}
              </a>
            ))}
          </nav>

          {activePerson && (
            <div className="space-y-3">
              {monthKeys.map((monthKey) => {
                const daysInMonth = [...personRowsByDate.entries()]
                  .filter(([date]) => date.slice(0, 7) === monthKey)
                  .sort((a, b) => a[0].localeCompare(b[0]));

                return (
                  <Card key={monthKey} id={`month-${monthKey}`} variant="static" className="p-5 space-y-3">
                    <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {monthLabel(monthKey)}
                    </h3>
                    {daysInMonth.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No work recorded this month.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {daysInMonth.map(([date, rows]) => {
                          const dayExpenditure = rows.reduce((s, r) => s + r.rdExpenditure, 0);
                          const dayHours = rows.reduce((s, r) => s + r.rdHours, 0);
                          const anySessionData = rows.some((r) => r.sessionCount !== undefined);
                          const daySessions = rows.reduce((s, r) => s + (r.sessionCount ?? 0), 0);
                          return (
                            <li key={date}>
                              <button
                                type="button"
                                onClick={() => setSelectedDay({ date, personName: activePerson.name, rows })}
                                className="flex w-full flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm hover:bg-accent"
                              >
                                <span className="flex items-center gap-2">
                                  <span className="font-mono tabular-nums">{date}</span>
                                  <span className="text-xs text-muted-foreground">{dowLabel(date)}</span>
                                </span>
                                <span className="flex items-center gap-3 font-mono tabular-nums text-xs text-muted-foreground">
                                  {anySessionData && <span>{daySessions} sess</span>}
                                  <span>{formatHours(dayHours)}</span>
                                  <span className="font-medium text-foreground">{formatCurrency(dayExpenditure)}</span>
                                  <Badge variant="default">{rows.length} project{rows.length === 1 ? '' : 's'}</Badge>
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      <TimesheetDayPanel day={selectedDay} onClose={() => setSelectedDay(null)} />
    </div>
  );
}
